import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Users, Search, Loader2, Plus, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { listAccountContacts } from "@/lib/contacts.functions";
import { listMyWorkspaces } from "@/lib/workspaces.functions";
import { formatDistanceToNow } from "date-fns";
import { EmptyState } from "@/components/empty-state";

export const Route = createFileRoute("/_authenticated/contacts")({
  component: ContactsPage,
});

const STATUS_TONES: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  paid: "default",
  refunded: "destructive",
  chargeback: "destructive",
  pending: "secondary",
  cancelled: "outline",
};

function fullName(c: { first_name: string | null; last_name: string | null; email: string | null }) {
  const n = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return n || c.email || ",";
}

function ContactsPage() {
  const listContacts = useServerFn(listAccountContacts);
  const listWorkspaces = useServerFn(listMyWorkspaces);

  const [search, setSearch] = useState("");
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  const wsQ = useQuery({ queryKey: ["my-workspaces"], queryFn: () => listWorkspaces() });
  const cQ = useQuery({
    queryKey: ["account-contacts", search, workspaceId],
    queryFn: () =>
      listContacts({
        data: { search: search || null, workspace_id: workspaceId, limit: 200 },
      }),
  });

  const workspaces = wsQ.data?.workspaces ?? [];
  const contacts = cQ.data?.contacts ?? [];
  const hasWorkspaces = workspaces.length > 0;
  const hasContacts = contacts.length > 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every customer across your workspaces, newest activity first.
        </p>
      </div>

      {!hasWorkspaces && !wsQ.isLoading ? (
        <EmptyState
          icon={Users}
          title="No workspaces yet"
          description="Contacts get created when you import CSVs. Create your first workspace and upload a report to populate this list."
          action={
            <Button asChild>
              <Link to="/workspaces">
                <Plus className="mr-2 h-4 w-4" />
                New workspace
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative max-w-xs flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email…"
                className="pl-8"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              <Button
                variant={workspaceId === null ? "default" : "outline"}
                size="sm"
                onClick={() => setWorkspaceId(null)}
              >
                All workspaces
              </Button>
              {workspaces.slice(0, 5).map((w) => (
                <Button
                  key={w.id}
                  variant={workspaceId === w.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setWorkspaceId(w.id)}
                >
                  {w.program_name}
                </Button>
              ))}
            </div>
          </div>

          {cQ.isLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading contacts…
            </div>
          ) : !hasContacts ? (
            <EmptyState
              icon={UserCircle2}
              variant={search ? "filtered" : "first-run"}
              title={search ? "No contacts match that search" : "Your contacts appear here"}
              description={
                search
                  ? "Try a different name or email."
                  : "Upload a CSV in any workspace to populate contacts here automatically."
              }
              action={
                search ? (
                  <Button variant="ghost" onClick={() => setSearch("")}>Clear search</Button>
                ) : (
                  <Button asChild>
                    <Link to="/imports">Go to imports</Link>
                  </Button>
                )
              }
            />

          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5">Contact</th>
                    <th className="px-4 py-2.5">Workspace</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Tier</th>
                    <th className="px-4 py-2.5 text-right">Commission</th>
                    <th className="px-4 py-2.5">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => {
                    const ws = (c as { workspaces?: { program_name?: string } | null }).workspaces;
                    const tone = STATUS_TONES[(c.status ?? "").toLowerCase()] ?? "outline";
                    return (
                      <tr key={c.id} className="border-b border-border/60 last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="font-medium">{fullName(c)}</div>
                          {c.email && c.email !== fullName(c) && (
                            <div className="text-xs text-muted-foreground">{c.email}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to="/workspaces/$workspaceId"
                            params={{ workspaceId: c.workspace_id as string }}
                            className="text-primary hover:underline"
                          >
                            {ws?.program_name ?? "Workspace"}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          {c.status ? <Badge variant={tone}>{c.status}</Badge> : <span className="text-muted-foreground">,</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{c.tier ?? ","}</td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {c.commission_amount != null
                            ? `$${Number(c.commission_amount).toFixed(2)}${c.is_recurring ? "/mo" : ""}`
                            : ","}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {c.date_created
                            ? formatDistanceToNow(new Date(c.date_created), { addSuffix: true })
                            : ","}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

